import { useEffect, useState } from 'react';

import type { BoardAgentAPI } from '../helpers/remoteBoardAgent';

/** Taken from the runner's own reply, not restated: a phase added there has
 *  to be answered for here rather than silently missing from LIVE. */
type DeployRunPhase = Awaited<
  ReturnType<BoardAgentAPI['deployRunState']>
>['phase'];

/** Phases where the deploy is still live and closing its surface only parks
 *  it. */
const LIVE: DeployRunPhase[] = ['running', 'awaiting-input', 'awaiting-auth'];

/**
 * The deploy run's phase for surfaces that are NOT the deploy console — the
 * board header's runner button and the runner window's Deploy tab label (web
 * and mobile), so a run whose console was closed still says it
 * is going.
 *
 * The runner owns the run, so this is one fetch per connected agent plus the
 * `deploy-run` event. Cheap enough to sit in a header that is always mounted,
 * and it survives the console closing, which is the whole point.
 */
export default function useDeployRunning(agent: BoardAgentAPI | null) {
  const [phase, setPhase] = useState<DeployRunPhase>('idle');

  useEffect(() => {
    if (!agent) {
      setPhase('idle');
      return;
    }
    let ignore = false;
    const load = () => {
      void agent
        .deployRunState()
        .then((run) => {
          if (!ignore) setPhase(run.phase);
        })
        // A runner that can't answer is a runner with nothing running.
        .catch(() => {
          if (!ignore) setPhase('idle');
        });
    };
    load();
    // The event carries a phase string, but a refetch keeps this in step with
    // the console, which refetches on the same signal.
    const unsubscribe = agent.onDeployRun(() => load());
    return () => {
      ignore = true;
      unsubscribe();
    };
  }, [agent]);

  return {
    phase,
    /** A deploy is in flight — the header should say so. */
    running: LIVE.includes(phase),
    /** It is in flight AND blocked on the user, so it needs them back. */
    blocked: phase === 'awaiting-input' || phase === 'awaiting-auth'
  };
}
